import { useEffect, useState } from 'react';
import { projectChapters } from '../constants';

export function useChapterNavigation(project) {
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    // Filter chapters based on what exists in the project
    const availableChapters = projectChapters.filter((chapter) => {
      if (chapter.id === 'overview') return true;
      if (chapter.id === 'the-problem') return project?.userResearch;
      if (chapter.id === 'challenge') return project?.challenge;
      if (chapter.id === 'results') return project?.results;
      if (chapter.id === 'visual-treatment') return project?.visualTreatment;
      if (chapter.id === 'final-design') return project?.finalDesign || project?.results?.hpImage || (project?.results?.finalDesignImages && project.results.finalDesignImages.length > 0) || (project?.results?.finalDesignRows && project.results.finalDesignRows.length > 0);
      if (chapter.id.startsWith('problem-')) {
        return project?.problems?.some(p => p.id === chapter.id);
      }
      if (chapter.id === 'achievements') return project?.achievements;
      if (chapter.id === 'reflections') return project?.reflections;
      if (chapter.id === 'design-process') return project?.designProcess;
      return false;
    });

    const observerOptions = {
      root: null,
      rootMargin: '-10% 0px -70% 0px',
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
    };

    const sectionStates = new Map();

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        const rect = entry.boundingClientRect;
        sectionStates.set(entry.target.id, {
          isIntersecting: entry.isIntersecting,
          ratio: entry.intersectionRatio,
          top: rect.top,
          bottom: rect.bottom,
          height: rect.height,
        });
      });

      // Algorithm: Find the section that is most prominently in the viewport
      // A section becomes active as soon as any part of it enters the viewport
      const scrollOffset = 128; // Account for scroll-mt-32
      const viewportTop = scrollOffset;
      
      let bestCandidate = null;
      let bestScore = Infinity;

      // Check all available sections
      availableChapters.forEach((chapter) => {
        const element = document.getElementById(chapter.id);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const sectionTop = rect.top;
        const sectionBottom = rect.bottom;
        const viewportHeight = window.innerHeight;
        
        // Check if section is at least partially visible in viewport
        const isPartiallyVisible = sectionBottom > 0 && sectionTop < viewportHeight;
        
        if (isPartiallyVisible) {
          // Calculate how much of the section is visible
          const visibleTop = Math.max(0, sectionTop);
          const visibleBottom = Math.min(viewportHeight, sectionBottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          const visibilityRatio = visibleHeight / Math.max(viewportHeight, rect.height);
          
          // Calculate distance from section top to ideal viewport top
          const distanceFromTop = Math.abs(sectionTop - viewportTop);
          
          // Score calculation:
          // Lower score = better candidate
          // Prioritize sections whose top is at or above viewport top
          let score = distanceFromTop;
          
          if (sectionTop <= viewportTop) {
            // Section top is at or above viewport top - this is the active section
            // Give it a very low score (high priority)
            score = distanceFromTop * 0.1;
            // Bonus for higher visibility
            score -= visibilityRatio * 50;
          } else if (sectionTop < viewportTop + 200) {
            // Section is just below viewport top - still a candidate
            score = distanceFromTop;
            // Reduce score based on visibility
            score -= visibilityRatio * 20;
          } else {
            // Section is further down - lower priority
            score = distanceFromTop * 2;
            score -= visibilityRatio * 10;
          }
          
          // Never select overview if we're scrolled down and other sections are visible
          if (chapter.id === 'overview' && sectionTop > viewportTop + 100) {
            // Overview is below the top and other sections might be visible
            // Only select it if no other section is a better candidate
            score += 1000; // Heavy penalty
          }
          
          if (score < bestScore) {
            bestScore = score;
            bestCandidate = chapter.id;
          }
        }
      });

      // Always update if we found a section
      // This ensures the active state flows with the page as user scrolls
      if (bestCandidate) {
        setActiveSection(bestCandidate);
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Only observe available chapters
    availableChapters.forEach((chapter) => {
      const element = document.getElementById(chapter.id);
      if (element) {
        observer.observe(element);
      }
    });

    // Also listen to scroll events to ensure continuous updates
    const handleScroll = () => {
      // Recalculate active section on scroll
      const scrollOffset = 128;
      const viewportTop = scrollOffset;
      
      let bestCandidate = null;
      let bestScore = Infinity;

      availableChapters.forEach((chapter) => {
        const element = document.getElementById(chapter.id);
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const sectionTop = rect.top;
        const sectionBottom = rect.bottom;
        const viewportHeight = window.innerHeight;
        
        const isPartiallyVisible = sectionBottom > 0 && sectionTop < viewportHeight;
        
        if (isPartiallyVisible) {
          const visibleTop = Math.max(0, sectionTop);
          const visibleBottom = Math.min(viewportHeight, sectionBottom);
          const visibleHeight = Math.max(0, visibleBottom - visibleTop);
          const visibilityRatio = visibleHeight / Math.max(viewportHeight, rect.height);
          
          const distanceFromTop = Math.abs(sectionTop - viewportTop);
          let score = distanceFromTop;
          
          if (sectionTop <= viewportTop) {
            score = distanceFromTop * 0.1;
            score -= visibilityRatio * 50;
          } else if (sectionTop < viewportTop + 200) {
            score = distanceFromTop;
            score -= visibilityRatio * 20;
          } else {
            score = distanceFromTop * 2;
            score -= visibilityRatio * 10;
          }
          
          if (chapter.id === 'overview' && sectionTop > viewportTop + 100) {
            score += 1000;
          }
          
          if (score < bestScore) {
            bestScore = score;
            bestCandidate = chapter.id;
          }
        }
      });

      if (bestCandidate) {
        setActiveSection(bestCandidate);
      }
    };

    // Throttle scroll events for performance
    let scrollTimeout;
    const throttledScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(handleScroll, 50);
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });

    return () => {
      availableChapters.forEach((chapter) => {
        const element = document.getElementById(chapter.id);
        if (element) {
          observer.unobserve(element);
        }
      });
      window.removeEventListener('scroll', throttledScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [project]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return { activeSection, scrollToSection };
}

